/**
 * Admin User Contracts - Database-First Zod Schemas
 *
 * SOURCE OF TRUTH: PostgreSQL stored procedures and tables
 * Generated from MCP queries on 2026-01-02
 *
 * Related DB objects:
 * - get_user_profile_v2(p_telegram_id bigint)
 * - ban_user_v2(p_telegram_id bigint, p_staff_id varchar, p_reason text)
 * - unban_user_v2(p_telegram_id bigint, p_staff_id varchar, p_reason text)
 * - recreate_game_account_v2(p_telegram_id bigint, p_staff_id varchar, p_reason text, p_old_game_code varchar, p_new_game_code varchar)
 * - get_ban_history(p_telegram_id bigint)
 * - get_recreate_history(p_telegram_id bigint)
 * - Tables: users, bans, staff
 */
import { z } from 'zod';
import { bigintString } from '../common.js';
// =============================================================================
// DATABASE CONSTRAINTS (from pg_constraint)
// =============================================================================
/**
 * Ban action values (matches bans.action CHECK constraint)
 * CHECK ((action)::text = ANY (ARRAY['ban', 'unban']))
 */
export const banActionValues = ['ban', 'unban'];
export const banActionSchema = z.enum(banActionValues);
// =============================================================================
// STORED PROCEDURE RESULTS
// =============================================================================
/**
 * get_user_profile_v2() result
 * Returns comprehensive user profile with merged stats
 *
 * SQL: SELECT * FROM get_user_profile_v2($1)
 */
export const getUserProfileResultSchema = z.object({
    telegram_id: z.coerce.bigint(),
    username: z.string().nullable(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    game_account_code: z.string().nullable(),
    is_banned: z.boolean(),
    has_deposited: z.boolean(),
    total_deposit: z.number().int(),
    total_bonus: z.number().int(),
    total_deposit_plus_bonus: z.number().int(),
    total_withdrawal: z.number().int(),
    net_total: z.number().int(),
    minimum_withdrawal: z.number().int(),
    last_deposit_ref: z.string().nullable(),
    referee_id: z.string().nullable(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
/**
 * ban_user_v2() result
 * Returns success status and cancelled transaction counts
 *
 * SQL: SELECT * FROM ban_user_v2($1, $2, $3)
 * Returns: TABLE(success boolean, message text, deposits_cancelled integer, withdrawals_declined integer)
 */
export const banUserResultSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    deposits_cancelled: z.number().int(),
    withdrawals_declined: z.number().int(),
});
/**
 * unban_user_v2() result
 *
 * SQL: SELECT * FROM unban_user_v2($1, $2, $3)
 * Returns: TABLE(success boolean, message text)
 */
export const unbanUserResultSchema = z.object({
    success: z.boolean(),
    message: z.string(),
});
/**
 * recreate_game_account_v2() result
 *
 * SQL: SELECT * FROM recreate_game_account_v2($1, $2, $3, $4, $5)
 * Returns: TABLE(success boolean, message text)
 */
export const recreateGameAccountResultSchema = z.object({
    success: z.boolean(),
    message: z.string(),
});
/**
 * get_ban_history() result row
 *
 * SQL: SELECT * FROM get_ban_history($1)
 * Returns: TABLE(id integer, staff_id varchar, action varchar, reason text, created_at timestamptz)
 */
export const banHistoryRowSchema = z.object({
    id: z.number().int(),
    staff_id: z.string(),
    action: banActionSchema,
    reason: z.string().nullable(),
    created_at: z.coerce.date(),
});
/**
 * get_recreate_history() result row
 *
 * SQL: SELECT * FROM get_recreate_history($1)
 * Returns: TABLE(id integer, staff_id varchar, old_game_code varchar, new_game_code varchar, reason text, created_at timestamptz)
 */
export const recreateHistoryRowSchema = z.object({
    id: z.number().int(),
    staff_id: z.string(),
    old_game_code: z.string().nullable(),
    new_game_code: z.string().nullable(),
    reason: z.string().nullable(),
    created_at: z.coerce.date(),
});
// =============================================================================
// API REQUEST/RESPONSE SCHEMAS
// =============================================================================
/**
 * GET /api/admin/users response data item
 * Matches backend controller response (searchUsers result)
 */
export const adminUserSummarySchema = z.object({
    telegramId: bigintString,
    username: z.string().nullable(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    isBanned: z.boolean(),
    gameAccountCode: z.string(),
    createdAt: z.string(), // ISO string
    totalDeposits: z.number().int(),
    totalWithdrawals: z.number().int(),
    lastDepositDate: z.string().nullable(), // ISO string or null
});
// =============================================================================
// BAN INFO SCHEMA (for detail view)
// =============================================================================
/**
 * Ban info for currently banned user
 * Returned when user.isBanned is true
 */
export const banInfoSchema = z.object({
    bannedBy: z.string(),
    reason: z.string().nullable(),
    bannedAt: z.string(), // ISO string
});
/**
 * Recreate info for users with recreate history
 */
export const recreateInfoSchema = z.object({
    recreatedBy: z.string(),
    reason: z.string().nullable(),
    oldGameCode: z.string().nullable(),
    newGameCode: z.string().nullable(),
    recreatedAt: z.string(), // ISO string
});
/**
 * Ban history entry (camelCase for API layer)
 */
export const banHistoryEntrySchema = z.object({
    id: z.number().int(),
    staffId: z.string(),
    action: banActionSchema,
    reason: z.string().nullable(),
    createdAt: z.string(), // ISO string
});
/**
 * Recreate history entry (camelCase for API layer)
 */
export const recreateHistoryEntrySchema = z.object({
    id: z.number().int(),
    staffId: z.string(),
    oldGameCode: z.string().nullable(),
    newGameCode: z.string().nullable(),
    reason: z.string().nullable(),
    createdAt: z.string(), // ISO string
});
// =============================================================================
// USER DETAIL RESPONSE
// =============================================================================
/**
 * GET /api/admin/users/:telegramId response data
 * Matches backend controller response
 *
 * NOTE: Backend uses LEGACY field names (currentDepositAmount, lifetimeTotalDeposits)
 * which map to the NEW database columns (total_deposit, total_withdrawal)
 */
export const userDetailResponseDataSchema = z.object({
    user: z.object({
        telegramId: bigintString,
        username: z.string().nullable(),
        firstName: z.string().nullable(),
        lastName: z.string().nullable(),
        isBanned: z.boolean(),
        gameAccountCode: z.string().nullable(),
        createdAt: z.coerce.date(),
    }),
    stats: z
        .object({
        // Legacy field names for backward compatibility
        // These map to the actual DB columns:
        currentDepositAmount: z.number().int(), // → total_deposit
        currentBonusAmount: z.number().int(), // → total_bonus
        currentMinimumWithdrawal: z.number().int(), // → minimum_withdrawal
        lifetimeTotalDeposits: z.number().int(), // → total_deposit (same as current in v4)
        lifetimeTotalWithdrawals: z.number().int(), // → total_withdrawal
        largeWithdrawalCount: z.number().int(), // → 0 (no longer tracked)
    })
        .nullable(),
    // Ban info (only present if user is currently banned)
    banInfo: banInfoSchema.nullable(),
    // Recreate info (only present if user was ever recreated)
    recreateInfo: recreateInfoSchema.nullable(),
    // Full history arrays
    banHistory: z.array(banHistoryEntrySchema),
    recreateHistory: z.array(recreateHistoryEntrySchema),
});
/**
 * POST /api/admin/users/:telegramId/ban request body
 */
export const banUserRequestSchema = z.object({
    reason: z.string().min(1, 'Reason is required'),
});
/**
 * POST /api/admin/users/:telegramId/recreate request body
 */
export const recreateUserRequestSchema = z.object({
    reason: z.string().min(1, 'Reason is required'),
});
/**
 * POST /api/admin/users/:telegramId/ban response data
 * NOTE: Backend currently only returns { message }, but should return full result
 */
export const banUserResponseDataSchema = z.object({
    message: z.string(),
    // These fields are returned by ban_user_v2 but not currently exposed by controller
    // depositsCancelled: z.number().int().optional(),
    // withdrawalsDeclined: z.number().int().optional(),
});
/**
 * POST /api/admin/users/:telegramId/unban request body
 */
export const unbanUserRequestSchema = z.object({
    reason: z.string().optional(),
});
/**
 * POST /api/admin/users/:telegramId/unban response data
 */
export const unbanUserResponseDataSchema = z.object({
    message: z.string(),
});
/**
 * POST /api/admin/users/:telegramId/recreate response data
 * Matches backend controller response
 */
export const recreateUserResponseDataSchema = z.object({
    message: z.string(),
    chc0GameCode: z.string().nullable(),
    oldGameCode: z.string().nullable(),
    newGameCode: z.string().nullable(),
    balanceWiped: z.number().int(),
    transactionsCancelled: z.number().int(),
});
//# sourceMappingURL=users.js.map